import { useState, useEffect, useRef, useCallback } from "react";
import { Upload, Image, message, ConfigProvider } from "antd";
import type { UploadFile, UploadProps } from "antd";
import { Trans } from "react-i18next";
import { t } from "i18next";
import { IconLoading } from "@icons/index";
import { uploadProductImage } from "@apis/upload";
import useUploadAgreement from "@hooks/useUploadAgreement";
import { processErrorMessage } from "@utils/errrorMessage";
import "@components/ViewAngleSelector/index.scss";
import "./index.scss";
import {
  IMAGE_UPLOAD_CONFIG,
  extractAxiosErrorMeta,
  getBase64,
  preprocessImageFiles,
  showImageUploadErrorToast,
} from "../hooks";
import type { ProductImage } from "@pages/videoCreating/videoCreatingSlice";
import { ImageTagSelector } from "./ImageTagSelector";
import { TestId } from "@shared/testIds";
import { ClipUploadPlaceholder } from "@components/ClipUpload/ClipUploadPlaceholder";

export interface MultiImageUploadProps {
  /** 最大图片数量 */
  maxCount?: number;
  /** 图片列表 */
  dataList?: ProductImage[];
  /** 图片列表变化回调 */
  onChange?: (images: ProductImage[]) => void;
  /** 上传状态变化回调 */
  onUploadingChange?: (uploading: boolean) => void;
  /** 卡片尺寸（宽高相同时使用） */
  cardSize?: number;
  /** 卡片宽度（优先级高于 cardSize） */
  cardWidth?: number;
  /** 卡片高度（优先级高于 cardSize） */
  cardHeight?: number;
}

const { ACCEPTED_MIME_TYPES, ACCEPTED_EXTENSIONS } = IMAGE_UPLOAD_CONFIG;

export const MultiImageUpload = ({
  maxCount = 6,
  dataList = [],
  onChange,
  onUploadingChange,
  cardSize = 158,
  cardWidth,
  cardHeight: _cardHeight,
}: MultiImageUploadProps) => {
  const width = cardWidth ?? cardSize;

  // 上传协议确认
  const { agreementModal, confirmAgreement } = useUploadAgreement();

  // 批次计数器：用于对同一批 beforeUpload 调用去重（多选 N 个文件时 beforeUpload 被调用 N 次）
  const batchCounterRef = useRef(0);

  // 用 ref 追踪最新的 dataList，避免并发上传时闭包陈旧
  const dataListRef = useRef(dataList);
  dataListRef.current = dataList;

  const dataListToFileList = (images: ProductImage[]): UploadFile[] =>
    images.map((img, index) => ({
      uid: String(img.id),
      name: `image-${index}`,
      status: "done" as const,
      description: img.description,
      url: img.url,
    }));

  const [fileList, setFileList] = useState<UploadFile[]>(() => dataListToFileList(dataList));
  const [uploading, _setUploading] = useState(false);
  const setUploading = useCallback(
    (val: boolean) => {
      _setUploading(val);
      onUploadingChange?.(val);
    },
    [onUploadingChange],
  );
  /** 每张图视角的展示文案（由 ViewAngleSelector onChange 抛出，避免在 selector 内根据 value 反算） */
  const [, _setAngleLabels] = useState<Record<string, string>>({});

  useEffect(() => {
    // 只有当 dataList 的 id 列表与 fileList 不一致时才更新，避免循环更新
    const dataListIds = dataList
      .map((img) => img.id)
      .sort()
      .join(",");
    const fileListIds = fileList
      .filter((f) => f.status === "done")
      .map((f) => f.uid)
      .sort()
      .join(",");

    if (dataListIds !== fileListIds) {
      setFileList(dataListToFileList(dataList));
    }
  }, [dataList]);

  // 预览状态
  const [previewOpen, setPreviewOpen] = useState(false);
  const [previewImage, setPreviewImage] = useState("");

  // 处理预览
  const handlePreview = async (file: UploadFile) => {
    const image = dataList.find((img) => String(img.id) === file.uid);
    if (image?.base64) {
      setPreviewImage(image.base64);
      setPreviewOpen(true);
      return;
    }
    if (!file.url && !file.preview) {
      file.preview = await getBase64(file.originFileObj as File);
    }
    setPreviewImage(file.url || (file.preview as string));
    setPreviewOpen(true);
  };

  // 处理文件列表变化
  const handleChange: UploadProps["onChange"] = ({ fileList: newFileList }) => {
    // 更新 fileList，同时处理上传成功后的 url
    const updatedFileList = newFileList.map((f) => {
      if (f.status === "done" && f.response && !f.url) {
        return { ...f, url: f.response.picPath };
      }
      return f;
    });
    setFileList(updatedFileList);

    // 同步删除到 dataList（只处理删除操作，新增由 customUpload 处理）
    const currentUids = updatedFileList.map((f) => f.uid);
    const newDataList = dataList.filter((img) => currentUids.includes(String(img.id)));
    if (newDataList.length !== dataList.length) {
      onChange?.(newDataList);
    }
  };

  const handleImageTagChange = (uid: string, description: string) => {
    const updated = dataList.map((img) =>
      String(img.id) === uid
        ? ({ ...img, description, aiAnalyzed: !!description && description.trim() !== "" } satisfies ProductImage)
        : img,
    );
    onChange?.(updated);
  };

  // 批量上传
  const uploadProductImages = useCallback(
    async (files: File[]) => {
      if (files.length === 0) return;
      setUploading(true);
      try {
        const results = await uploadProductImage(files);

        const failed = results.filter((r) => !r.success);
        if (failed.length > 0) {
          const reason = failed.find((r) => r.rejectionReason)?.rejectionReason;
          message.error(reason || t("Image upload failed, please refer to the requirements and re-upload"));
        }

        const succeeded = results.filter((r) => r.success && r.imageUrl);
        if (succeeded.length === 0) return;

        const newImageList: ProductImage[] = succeeded.map<ProductImage>(
          (item) =>
            ({
              id: `product-image-${item.assetId}`,
              assetId: item.assetId,
              url: item.imageUrl,
              description: "",
              isChangedUrl: true,
              aiAnalyzed: false,
            }) satisfies ProductImage,
        );
        const latestDataList = dataListRef.current;
        const remainingSlots = maxCount - latestDataList.length;
        onChange?.([...latestDataList, ...newImageList.slice(0, remainingSlots)]);
      } catch (error) {
        const meta = extractAxiosErrorMeta(error);
        const processed = processErrorMessage(error);
        if (!processed) {
          showImageUploadErrorToast(IMAGE_UPLOAD_CONFIG.DEFAULT_ERROR_KEY, meta.traceId);
        }
      } finally {
        setUploading(false);
      }
    },
    [onChange],
  );

  // antd Upload 配置
  const uploadProps: UploadProps = {
    name: "file",
    listType: "picture-card",
    fileList,
    maxCount,
    multiple: true,
    accept: [...ACCEPTED_MIME_TYPES, ...ACCEPTED_EXTENSIONS].join(","),
    onPreview: handlePreview,
    onChange: handleChange,
    beforeUpload: async (_, fileList) => {
      // 多选 N 个文件时 beforeUpload 会被调用 N 次，利用批次计数器确保只处理一次
      const currentBatch = ++batchCounterRef.current;
      // 等一个微任务，让同批次的所有 beforeUpload 调用都递增计数器
      await Promise.resolve();
      // 仅最后一次调用（计数器未被后续调用覆盖）继续执行
      if (currentBatch !== batchCounterRef.current) return Upload.LIST_IGNORE;

      const remainingSlots = maxCount - dataListRef.current.length;
      if (remainingSlots <= 0) return Upload.LIST_IGNORE;
      const filesToUpload = fileList.slice(0, remainingSlots);

      // 校验并预处理图片文件（超限时自动压缩）
      const preprocessedFiles = await preprocessImageFiles(filesToUpload, {
        compressOutputType: IMAGE_UPLOAD_CONFIG.COMPRESS_OUTPUT_TYPE,
      });
      if (!preprocessedFiles) return Upload.LIST_IGNORE;
      // 确认上传协议
      const confirmed = await confirmAgreement();
      if (!confirmed) return Upload.LIST_IGNORE;

      await uploadProductImages(preprocessedFiles);
      return Upload.LIST_IGNORE;
    },
    // 自定义每个项目的渲染（添加描述输入框和上传中遮罩）
    itemRender: (originNode, file) => {
      const image = dataList.find((img) => String(img.id) === file.uid);
      const { description } = image ?? {};
      const isUploading = file.status === "uploading";
      const isDone = file.status === "done";
      const disabled = isUploading || file.status !== "done";

      return (
        <div
          className="flex h-full w-full flex-col gap-[4px] overflow-hidden rounded-[8px] bg-kds-bg-subtle p-[8px] pb-0"
          data-testid={TestId.VideoImageUploadItem}
        >
          <div className="relative min-h-0 h-[142px] w-[142px] shrink-0 overflow-hidden">
            {originNode}
            {/* 上传中遮罩 */}
            {isUploading && (
              <div className="absolute inset-0 bg-auth-mask-bg flex flex-col items-center justify-center gap-[8px] rounded-[8px]">
                <IconLoading className="w-[24px] h-[24px] animate-spin text-white" />
                <span className="text-[14px] text-white">
                  <Trans>Uploading...</Trans>
                </span>
              </div>
            )}
          </div>
          {isDone && (
            <ImageTagSelector
              value={description}
              disabled={disabled}
              onChange={(description) => handleImageTagChange(file.uid, description)}
            />
          )}
          {/* <ViewAngleSelector
            value={image?.description}
            displayLabel={angleLabels[file.uid]}
            disabled={isUploading || file.status !== "done"}
            width={width}
            onChange={(v, label) => handleDescriptionChange(file.uid, v, label)}
          /> */}
        </div>
      );
    },
  };

  // 上传按钮（包含描述输入框占位，与图片卡片高度对齐）
  const uploadButton = <ClipUploadPlaceholder />;

  const uploadingPlaceholder = (
    <div className="flex flex-col gap-[8px] bg-kds-bg-subtle border border-kds-border rounded-[8px] overflow-hidden w-full h-full">
      <div className="flex flex-col items-center justify-center gap-[8px] bg-kds-bg-element rounded-[4px]  w-full h-full">
        <IconLoading className="w-[24px] h-[24px] animate-spin text-kds-fg-subtle" />
      </div>
    </div>
  );

  return (
    <>
      <ConfigProvider theme={{ components: { Upload: { pictureCardSize: width } } }}>
        <Upload
          {...uploadProps}
          className="multi-image-upload"
          styles={{
            root: {
              ["--multi-image-card-height"]: "186px",
            } as React.CSSProperties,
          }}
        >
          {uploading ? uploadingPlaceholder : fileList.length < maxCount ? uploadButton : null}
        </Upload>
      </ConfigProvider>
      {/* 图片预览弹窗 */}
      {previewImage && (
        <Image
          wrapperStyle={{ display: "none" }}
          preview={{
            open: previewOpen,
            onOpenChange: (open) => setPreviewOpen(open),
          }}
          src={previewImage}
        />
      )}
      {/* 上传协议确认弹窗 */}
      {agreementModal}
    </>
  );
};
